// Delivery date and CTA helpers for SharePage.
// All times are in Asia/Almaty (UTC+5, no DST).

export function getNowKZ(now = new Date()) {
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  return new Date(utcMs + 5 * 3600 * 1000);
}

/**
 * Returns the current ordering status:
 *   'weekday_before'   — Mon–Fri, before 18:00
 *   'weekday_after'    — Mon–Fri, after 18:00
 *   'saturday_before'  — Saturday, before 13:00
 *   'saturday_after'   — Saturday, after 13:00
 *   'sunday'           — Sunday (any time)
 */
export function getDeliveryStatus(now = new Date()) {
  const kz = getNowKZ(now);
  const dow = kz.getDay(); // 0=Sun, 1=Mon … 6=Sat
  const mins = kz.getHours() * 60 + kz.getMinutes();

  if (dow === 0) return 'sunday';
  if (dow === 6) return mins < 13 * 60 ? 'saturday_before' : 'saturday_after';
  return mins < 18 * 60 ? 'weekday_before' : 'weekday_after';
}

/**
 * Returns a Date (midnight KZ) for the estimated delivery date, or null if unconfirmed.
 *
 * Rules:
 *  - Saturday before 13:00 → always next Monday (supplier doesn't ship on weekends).
 *  - Saturday after 13:00 or Sunday → null (need Monday confirmation).
 *  - Mon–Fri before 18:00 → base = today, add delivery_days calendar days.
 *  - Mon–Fri after 18:00  → base = next business day, add delivery_days.
 *  - If the resulting date is Sunday → shift to Monday.
 */
export function calculateEstimatedDeliveryDate(delivery_days, now = new Date()) {
  if (!delivery_days || delivery_days <= 0) return null;

  const kz = getNowKZ(now);
  const dow = kz.getDay();
  const mins = kz.getHours() * 60 + kz.getMinutes();
  const today = new Date(kz.getFullYear(), kz.getMonth(), kz.getDate());

  if (dow === 0) return null;

  if (dow === 6) {
    if (mins < 13 * 60) {
      const monday = new Date(today);
      monday.setDate(monday.getDate() + 2);
      return monday;
    }
    return null;
  }

  // Mon–Fri: find the base date
  const base = new Date(today);
  if (mins >= 18 * 60) {
    base.setDate(base.getDate() + 1);
    if (base.getDay() === 6) base.setDate(base.getDate() + 2); // Fri → Mon
  }

  const result = new Date(base);
  result.setDate(result.getDate() + delivery_days);
  if (result.getDay() === 0) result.setDate(result.getDate() + 1); // skip Sunday

  return result;
}

// ── Internal helpers ─────────────────────────────────────────────────────────

const MONTHS_RU = [
  'января','февраля','марта','апреля','мая','июня',
  'июля','августа','сентября','октября','ноября','декабря',
];

function formatDays(days) {
  if (days === 1) return '1 день';
  if (days >= 2 && days <= 4) return `${days} дня`;
  return `${days} дней`;
}

function isTomorrow(date, now) {
  const kz = getNowKZ(now);
  const tom = new Date(kz.getFullYear(), kz.getMonth(), kz.getDate() + 1);
  return date.getTime() === tom.getTime();
}

// True when base+delivery_days would have been Sunday (shifted to Monday).
function wasSundayShifted(delivery_days, now) {
  const kz = getNowKZ(now);
  const dow = kz.getDay();
  const mins = kz.getHours() * 60 + kz.getMinutes();
  const today = new Date(kz.getFullYear(), kz.getMonth(), kz.getDate());

  const base = new Date(today);
  if (dow >= 1 && dow <= 5 && mins >= 18 * 60) {
    base.setDate(base.getDate() + 1);
    if (base.getDay() === 6) base.setDate(base.getDate() + 2);
  }

  const raw = new Date(base);
  raw.setDate(raw.getDate() + delivery_days);
  return raw.getDay() === 0;
}

// "завтра" | "в понедельник" | "8 июня"
function formatDateHint(estimatedDate, delivery_days, now) {
  if (isTomorrow(estimatedDate, now)) return 'завтра';
  if (wasSundayShifted(delivery_days, now)) return 'в понедельник';
  return `${estimatedDate.getDate()} ${MONTHS_RU[estimatedDate.getMonth()]}`;
}

// ── Public formatters ────────────────────────────────────────────────────────

/**
 * CTA badge text (PromoBlock / CtaStrip).
 */
export function formatOrderCutoffCta(delivery_days, now = new Date()) {
  const status = getDeliveryStatus(now);

  if (status === 'saturday_before') return 'Закажите сегодня до 13:00 — привезём в понедельник';
  if (status === 'saturday_after' || status === 'sunday')
    return 'Оставьте заявку — цену, наличие и срок подтвердим в понедельник';
  if (status === 'weekday_after')
    return 'Оставьте заявку — мы заново проверим цену, наличие и срок';

  // weekday_before
  if (!delivery_days || delivery_days <= 0)
    return 'Закажите до 18:00 — срок поставки уточним при подтверждении';

  const estimatedDate = calculateEstimatedDeliveryDate(delivery_days, now);
  if (!estimatedDate)
    return 'Закажите до 18:00 — срок поставки уточним при подтверждении';

  const hint = formatDateHint(estimatedDate, delivery_days, now);
  if (hint === 'завтра') return 'Закажите до 18:00 — привезём завтра';
  if (hint === 'в понедельник') return 'Закажите до 18:00 — привезём в понедельник';
  return `Закажите до 18:00 — привезём через ${formatDays(delivery_days)}`;
}

/**
 * Inline suffix for "Срок поставки в магазин — …" in InfoBlock.
 * Returns null when delivery is unconfirmed.
 */
export function formatEstimatedDeliveryLabel(delivery_days, now = new Date()) {
  const status = getDeliveryStatus(now);

  if (status === 'saturday_after' || status === 'sunday') return null;
  if (!delivery_days || delivery_days <= 0) return null;

  const estimatedDate = calculateEstimatedDeliveryDate(delivery_days, now);
  if (!estimatedDate) return null;

  if (status === 'saturday_before') return 'ориентировочно в понедельник';

  const hint = formatDateHint(estimatedDate, delivery_days, now);
  return `${formatDays(delivery_days)}, ориентировочно ${hint}`;
}

/**
 * Step-5 text "Забрать товар можно…"
 */
export function formatPickupText(delivery_days, now = new Date()) {
  const status = getDeliveryStatus(now);

  if (status === 'saturday_after' || status === 'sunday' || status === 'weekday_after')
    return 'Забрать товар можно в магазине — срок уточним при подтверждении';
  if (status === 'saturday_before')
    return 'Забрать товар можно в магазине в понедельник';
  if (!delivery_days || delivery_days <= 0)
    return 'Забрать товар можно в магазине — срок уточним при подтверждении';

  const estimatedDate = calculateEstimatedDeliveryDate(delivery_days, now);
  if (!estimatedDate)
    return 'Забрать товар можно в магазине — срок уточним при подтверждении';

  const hint = formatDateHint(estimatedDate, delivery_days, now);
  return `Забрать товар можно в магазине ${hint}`;
}

/**
 * Warning/notice shown at the bottom of InfoBlock.
 */
export function formatCutoffNotice(now = new Date()) {
  const status = getDeliveryStatus(now);
  switch (status) {
    case 'weekday_before':
      return 'Скидка и цены действуют до 18:00 текущего дня. После 18:00 цену, наличие и срок поставки нужно будет проверить заново.';
    case 'saturday_before':
      return 'Скидка и цены действуют до 13:00 текущего дня. После 13:00 цену, наличие и срок поставки нужно будет проверить заново. Поставка будет в понедельник.';
    case 'weekday_after':
      return 'Сегодня время приема заявок по этой цене уже прошло. Оставьте заявку, и мы заново проверим цену, наличие и срок поставки.';
    case 'saturday_after':
      return 'Сегодня заявки по этой цене принимались до 13:00. Оставьте заявку, и мы подтвердим актуальную цену, наличие и срок поставки в понедельник.';
    case 'sunday':
      return 'Сегодня поставки со склада нет. Оставьте заявку, и мы подтвердим актуальную цену, наличие и срок поставки в понедельник.';
    default:
      return '';
  }
}

/**
 * Cutoff time string for step-3 label.
 */
export function formatCutoffTime(now = new Date()) {
  const status = getDeliveryStatus(now);
  return status === 'saturday_before' ? '13:00' : '18:00';
}

/**
 * True when the product list should be hidden and an "expired" notice shown.
 * Only on weekday after 18:00 (prices may have changed; need fresh quote).
 */
export function isOrderingExpired(now = new Date()) {
  return getDeliveryStatus(now) === 'weekday_after';
}
